// Seiva ECharts theme — built from live CSS custom properties.
//
// `buildSeivaTheme()` reads the design tokens from `:root` (and the `html.dark`
// re-binds + partnership branding overrides) via getComputedStyle, so the same
// theme adapts to light/dark and white-label colors with zero duplication. The
// per-series `color` palette stays on the fixed brand/categorical hues
// (primitives don't re-bind), while text/axis/tooltip surfaces use the aliases
// that DO re-bind in dark mode.
//
// The `ECharts` hook re-registers this theme and re-inits its chart whenever the
// `dark` class flips on <html> (see hooks/echarts.js).
import * as echarts from "echarts"
import { cssVar } from "./utils/css_tokens"

export function buildSeivaTheme() {
  const aios = typeof document !== "undefined" && document.documentElement.classList.contains("aios-document")
  // Categorical series palette — Mogno coral + navy + semantic + neutral accents.
  // Primitives (constant across themes) so a series keeps its hue in dark mode.
  const legacySeries = [
    cssVar("--s-500", "#FF826B"),
    cssVar("--info", "#0072CE"),
    cssVar("--warn", "#C9882B"),
    cssVar("--success", "#1F8A5B"),
    cssVar("--danger", "#D64A3B"),
    cssVar("--s-300", "#FFB2A6"),
    cssVar("--n-600", "#374145"),
    cssVar("--n-400", "#8195A1"),
  ]
  const series = aios ? [1,2,3,4,5].map((index) => cssVar(`--viz-cat-${index}`, legacySeries[index-1])) : legacySeries

  // Theme-aware surfaces — aliases that re-bind under html.dark.
  const text = cssVar("--text-primary", "#032F53")
  const textSecondary = cssVar("--text-secondary", "#374145")
  const border = cssVar("--border-default", "#DFE7ED")
  const splitLine = aios ? cssVar("--viz-grid", border) : cssVar("--border-default", "#DFE7ED")
  const cardBg = cssVar("--bg-card", "#FFFFFF")
  const fontFamily = aios ? cssVar("--font-text", "sans-serif") : "inherit"

  return {
    color: series,
    backgroundColor: "transparent",
    textStyle: { color: text, fontFamily },
    title: {
      textStyle: { color: text, fontWeight: aios ? 500 : 600, fontFamily: aios ? cssVar("--font-display", fontFamily) : fontFamily },
      subtextStyle: { color: textSecondary },
    },
    legend: {
      textStyle: { color: textSecondary },
    },
    tooltip: {
      appendToBody: true,
      backgroundColor: cardBg,
      borderColor: border,
      borderWidth: 1,
      textStyle: { color: text },
      extraCssText: `z-index:${cssVar("--z-tooltip", "2147483647")}!important;` + (aios ? `box-shadow:${cssVar("--shadow-md", "none")};border-radius:${cssVar("--radius-control", "8px")};` : "box-shadow: 0 4px 12px rgba(3,47,83,.10); border-radius: 10px;"),
    },
    axisPointer: { lineStyle: { color: border } },
    categoryAxis: {
      axisLine: { lineStyle: { color: border } },
      axisTick: { lineStyle: { color: border } },
      axisLabel: { color: textSecondary },
      splitLine: { lineStyle: { color: splitLine } },
    },
    valueAxis: {
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: textSecondary },
      splitLine: { lineStyle: { color: splitLine, type: "dashed" } },
    },
    timeAxis: {
      axisLine: { lineStyle: { color: border } },
      axisTick: { lineStyle: { color: border } },
      axisLabel: { color: textSecondary },
      splitLine: { lineStyle: { color: splitLine, type: "dashed" } },
    },
    line: {
      smooth: true,
      symbol: "circle",
      symbolSize: aios ? 8 : 6,
      lineStyle: { width: 2 },
    },
    bar: {
      barMaxWidth: 48,
      itemStyle: { borderRadius: [4, 4, 0, 0] },
    },
    gauge: {
      axisLine: { lineStyle: { color: [[1, splitLine]] } },
      axisTick: { lineStyle: { color: border } },
      axisLabel: { color: textSecondary },
      pointer: { itemStyle: { color: aios ? series[0] : cssVar("--s-500", "#FF826B") } },
      title: { color: text },
      detail: { color: text },
    },
  }
}

// Initial registration at load time (light mode). The hook re-registers on the
// dark toggle so the theme tracks the live custom properties.
echarts.registerTheme("seiva", buildSeivaTheme())
